import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import type { Program } from '@/lib/types';
import { cn, fmtDate, fmtRelative } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, Input, Textarea } from '@/components/ui/field';
import { Alert, Badge, Card, Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';

interface Member {
  membershipId: string;
  userId: string;
  name: string;
  email: string;
  role: string;
  title: string | null;
  budgetAccess: boolean;
  mfaEnabled: boolean;
  disabled: boolean;
  lastSignIn: string | null;
}
interface ReviewState {
  currentQuarter: string;
  doneThisQuarter: boolean;
  nextDue: { quarter: string; by: string };
  members: Member[];
  reviews: Array<{
    id: string;
    quarter: string;
    completedAt: string;
    completedBy: string;
    notes: string | null;
    checklist: Array<Member & { keep: boolean; note: string | null }>;
  }>;
}

export function AccessReview({ program, meId }: { program: Program; meId?: string }) {
  const base = `/api/programs/${program.id}/admin/access-review`;
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['admin', program.id, 'access-review'], queryFn: () => api.get<ReviewState>(base) });
  const [remove, setRemove] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const done = useMutation({
    mutationFn: () =>
      api.post<{ removed: number }>(base, {
        decisions: q.data!.members.map((m) => ({
          membershipId: m.membershipId,
          keep: !(m.membershipId in remove),
          ...(m.membershipId in remove ? { note: remove[m.membershipId] } : {}),
        })),
        ...(notes.trim() ? { notes } : {}),
      }),
    onSuccess: (r) => {
      toast.ok(r.removed ? `Review recorded. ${r.removed} removed and signed out.` : 'Review recorded.');
      setRemove({});
      setNotes('');
      void qc.invalidateQueries({ queryKey: ['admin', program.id] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!q.data) return <Spinner className="h-48" />;
  const r = q.data;
  const removing = Object.keys(remove);
  const missingReason = removing.some((id) => !remove[id]!.trim());

  return (
    <div className="space-y-5">
      {r.doneThisQuarter ? (
        <Alert tone="ok" title={`${r.currentQuarter} review recorded`}>
          The next review is due by {fmtDate(r.nextDue.by)} ({r.nextDue.quarter}).
        </Alert>
      ) : (
        <Alert tone="warn" title={`${r.currentQuarter} access review is due by ${fmtDate(r.nextDue.by)}`}>
          Confirm that everyone below still needs access. Agreement §9 calls for a quarterly review, logged when done.
        </Alert>
      )}

      {!r.doneThisQuarter && (
        <Card className="p-0">
          <table className="w-full text-[13px]">
            <thead className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">Member</th>
                <th className="px-2 py-2 font-medium">Role</th>
                <th className="px-2 py-2 font-medium">Two-factor</th>
                <th className="px-2 py-2 font-medium">Last sign-in</th>
                <th className="px-2 py-2 font-medium">Budget</th>
                <th className="w-72 px-4 py-2 font-medium">Decision</th>
              </tr>
            </thead>
            <tbody>
              {r.members.map((m) => {
                const out = m.membershipId in remove;
                const self = m.userId === meId;
                return (
                  <tr
                    key={m.membershipId}
                    className={cn('border-b border-border last:border-0 align-top', out && 'bg-bad-soft/40')}
                  >
                    <td className="px-4 py-2">
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-muted">{m.email}</div>
                    </td>
                    <td className="px-2 py-2">
                      {m.role.toLowerCase()}
                      {m.title ? <div className="text-xs text-muted">{m.title}</div> : null}
                    </td>
                    <td className="px-2 py-2">
                      {m.mfaEnabled ? <Badge tone="ok">on</Badge> : <Badge tone="warn">off</Badge>}
                    </td>
                    <td className="px-2 py-2 text-muted">{m.lastSignIn ? fmtRelative(m.lastSignIn) : 'Never'}</td>
                    <td className="px-2 py-2 text-muted">{m.budgetAccess ? 'Yes' : ''}</td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant={out ? 'secondary' : 'primary'}
                          onClick={() => {
                            const next = { ...remove };
                            delete next[m.membershipId];
                            setRemove(next);
                          }}
                        >
                          Keep
                        </Button>
                        <Button
                          size="sm"
                          variant={out ? 'danger' : 'ghost'}
                          disabled={self}
                          title={self ? 'You cannot remove yourself' : undefined}
                          onClick={() => setRemove({ ...remove, [m.membershipId]: remove[m.membershipId] ?? '' })}
                        >
                          Remove
                        </Button>
                      </div>
                      {out && (
                        <Input
                          className="mt-1.5 h-8 text-[13px]"
                          placeholder="Reason"
                          aria-label={`Reason for removing ${m.name}`}
                          value={remove[m.membershipId]}
                          onChange={(e) => setRemove({ ...remove, [m.membershipId]: e.target.value })}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="space-y-3 border-t border-border px-4 py-3">
            <Field label="Notes" htmlFor="ar-notes" hint="Optional, such as who reviewed it with you.">
              <Textarea id="ar-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
            <Button onClick={() => done.mutate()} loading={done.isPending} disabled={missingReason}>
              <ShieldCheck /> Complete the {r.currentQuarter} review
              {removing.length ? ` and remove ${removing.length}` : ''}
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-0">
        <h2 className="border-b border-border px-4 py-2.5 text-sm font-semibold">Completed reviews</h2>
        {!r.reviews.length ? (
          <p className="px-4 py-3 text-sm text-muted">None yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {r.reviews.map((x) => {
              const removed = x.checklist.filter((c) => !c.keep);
              return (
                <li key={x.id} className="px-4 py-2.5 text-[13px]">
                  <span className="font-medium">{x.quarter}</span>
                  <span className="text-muted">
                    {' '}
                    · {fmtDate(x.completedAt)} by {x.completedBy} · {x.checklist.length} reviewed
                    {removed.length ? `, ${removed.length} removed (${removed.map((c) => c.name).join(', ')})` : ''}
                  </span>
                  {x.notes && <p className="text-xs text-muted">{x.notes}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
